import React, { useEffect, useState } from 'react';
import '../../index.css';
import { useAuth } from "../../context/authContext";
import { Card, CardContent, CardHeader } from "../ui/card";
import { getPaymentDetails } from '../../services/payment.service';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useResolvedPaymentParams } from '../../hooks/useResolvedPaymentParams';
import { ArrowDown, ArrowUp, ArrowUpDown, List } from 'lucide-react';
import { PaymentDetailsCardSkeleton } from '../ui/card-skeletons';
import AsyncContent from '../ui/AsyncContent';

const currencyFormat = (value) => {
    return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

const convertAccountDetailsType = (type) => {
    switch (type) {
        case 'IBAN':
            return 'IBAN';
        case 'BIC':
            return 'BIC';
        case 'ACCOUNT_NO':
            return 'Account Number';
        case 'ACCOUNT_NUMBER':
                return 'Account Number';
        case 'SORT_CODE':
            return 'Sort Code';
        case 'ROUTING_NUMBER':
            return 'Routing Number';
        case 'SWIFT_CODE':
            return 'SWIFT Code';
        case 'ROUTING_NO':
            return 'Routing Number';
        default:
            return '';
    }
};

const isChequePayment = (payment) => {
    const chequePaymentTypes = ['CHECK_US', 'CHECK_CA'];
    return chequePaymentTypes.includes(payment.paymentMethod);
};

const SortIcon = ({ active, direction }) => {
    if (!active) {
        return <ArrowUpDown className="ml-1.5 inline h-3.5 w-3.5 opacity-40" />;
    }
    return direction === 'asc'
        ? <ArrowUp className="ml-1.5 inline h-3.5 w-3.5" />
        : <ArrowDown className="ml-1.5 inline h-3.5 w-3.5" />;
};

const SortableTableHead = ({ label, sortKey, sortConfig, onSort, className }) => {
    const active = sortConfig.key === sortKey;
    const alignRight = className?.includes('text-right');
    return (
        <TableHead className={className}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className={`inline-flex items-center font-medium text-slate-500 hover:text-slate-800 ${alignRight ? 'w-full justify-end' : ''}`}
            >
                {label}
                <SortIcon active={active} direction={sortConfig.direction} />
            </button>
        </TableHead>
    );
};

const getSortValue = (payment, key) => {
    switch (key) {
        case 'payee':
            return payment.payee?.name ?? '';
        case 'account':
            return payment.payee?.accountIdentifications?.[0]?.identification ?? '';
        case 'branch':
            return payment.payee?.accountIdentifications?.[1]?.identification ?? '';
        case 'chequeNo':
            return payment.chequeNo ?? '';
        case 'reference':
            return payment.reference ?? '';
        case 'amount':
            return payment.amount?.amount ?? 0;
        case 'currency':
            return payment.amount?.currency ?? '';
        default:
            return '';
    }
};

const getSortedPayments = (payments, sortConfig) => {
    if (!sortConfig.key) return payments;

    return [...payments].sort((a, b) => {
        const aVal = getSortValue(a, sortConfig.key);
        const bVal = getSortValue(b, sortConfig.key);

        const comparison = typeof aVal === 'number' && typeof bVal === 'number'
            ? aVal - bVal
            : String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base', numeric: true });

        return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
};

const PaymentDetails = ({ payment: paymentProp }) => {
    const { authenticated, user } = useAuth();
    const [payment, setPayment] = useState(paymentProp ?? null);
    const [loading, setLoading] = useState(!paymentProp);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const { resolvedPaymentId, resolvedOrgId } = useResolvedPaymentParams();

    useEffect(() => {
        if (paymentProp) {
            setPayment(paymentProp);
            setLoading(false);
            return;
        }

        const fetchPaymentDetails = async () => {
            setLoading(true);
            const response = await getPaymentDetails(user.token, resolvedPaymentId, resolvedOrgId);
            if (response && response.status === 200) {
                setPayment(response.data.data);
            }
            setLoading(false);
        };

        fetchPaymentDetails();
    }, [paymentProp, resolvedPaymentId, resolvedOrgId, user.token]);

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const renderPaymentData = (paymentData) => {
        if (paymentData.payments) {
            const sortedPayments = getSortedPayments(paymentData.payments, sortConfig);

            return (
                <Table>
                    <TableHeader>
                        <TableRow className="hover:bg-transparent border-slate-200">
                            <SortableTableHead
                                label="Payee"
                                sortKey="payee"
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                            {/* {paymentData.payments[0].payee.accountIdentifications[0] ? <TableHead>{convertAccountDetailsType(paymentData.payments[0].payee.accountIdentifications[0].type)}</TableHead> : null}
                            {paymentData.payments[0].payee.accountIdentifications[1] ? <TableHead>{convertAccountDetailsType(paymentData.payments[0].payee.accountIdentifications[1].type)}</TableHead> : null} */}
                            <SortableTableHead
                                label="Account No / IBAN"
                                sortKey="account"
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label="Branch No / BIC"
                                sortKey="branch"
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                            {isChequePayment(paymentData) ? (
                                <SortableTableHead
                                    label="Cheque No"
                                    sortKey="chequeNo"
                                    sortConfig={sortConfig}
                                    onSort={handleSort}
                                />
                            ) : null}
                            <SortableTableHead
                                label="Reference"
                                sortKey="reference"
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                            <SortableTableHead
                                label="Amount"
                                sortKey="amount"
                                sortConfig={sortConfig}
                                onSort={handleSort}
                                className="text-right"
                            />
                            <SortableTableHead
                                label="Currency"
                                sortKey="currency"
                                sortConfig={sortConfig}
                                onSort={handleSort}
                            />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedPayments.map((payment) => (
                            <TableRow key={payment.paymentId} className="hover:bg-slate-50/50">
                                <TableCell>{payment.payee.name}</TableCell>
                                <TableCell>{payment.payee.accountIdentifications?.[0]?.identification ?? null}</TableCell>
                                <TableCell>{payment.payee.accountIdentifications?.[1]?.identification ?? null}</TableCell>
                                {isChequePayment(paymentData) ? <TableCell>{payment.chequeNo || ''}</TableCell> : null}
                                <TableCell>{payment.reference}</TableCell>
                                <TableCell className="text-right">{currencyFormat(payment.amount.amount)}</TableCell>
                                <TableCell>{payment.amount.currency}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            );
        }
    };

    const card = (
        <Card className="overflow-hidden">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 border border-slate-200">
                        <List className="h-4 w-4 text-slate-500" />
                    </span>
                    <h3 className="my-0">Payment details</h3>
                </div>
            </CardHeader>
            <CardContent className="pt-4">
                {authenticated && payment ? (
                    <div className="overflow-x-auto">
                        {renderPaymentData(payment)}
                    </div>
                ) : (
                    <div className="blur select-none">
                        <p>Details cannot be displayed.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );

    if (paymentProp) {
        return <div className="w-full">{card}</div>;
    }

    return (
        <div className="w-full">
            <AsyncContent loading={loading} skeleton={<PaymentDetailsCardSkeleton />}>
                {card}
            </AsyncContent>
        </div>
    );
};

export default PaymentDetails;
